import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';

const ButtonWithHoverEffect = ({ 
  children, 
  className = '', 
  href, 
  target, 
  rel,
  onClick 
}) => {
  const buttonRef = useRef(null);
  const rippleRef = useRef(null);
  
  useEffect(() => {
    const button = buttonRef.current;
    const ripple = rippleRef.current;
    
    // Hide ripple initially
    gsap.set(ripple, { scale: 0, opacity: 0 });
    
    const handleMouseEnter = (e) => {
      const rect = button.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Calculate the required scale based on farthest corner from hover point
      const toCorners = [
        Math.hypot(x, y), // Distance to top-left
        Math.hypot(rect.width - x, y), // Distance to top-right
        Math.hypot(x, rect.height - y), // Distance to bottom-left
        Math.hypot(rect.width - x, rect.height - y) // Distance to bottom-right
      ];
      
      const maxDistance = Math.max(...toCorners);
      const rippleSize = 20; // Base size of ripple
      const requiredScale = (maxDistance * 2) / rippleSize;
      
      // Position the ripple at mouse position
      gsap.set(ripple, {
        x: x,
        y: y,
        opacity: 1,
        scale: 0,
      });
      
      // Animate ripple with calculated scale
      gsap.to(ripple, {
        scale: requiredScale,
        duration: 0.6,
        ease: "power2.out"
      });
    };
    
    const handleMouseLeave = () => {
      gsap.to(ripple, {
        opacity: 0,
        duration: 0.4,
        ease: "power2.in"
      });
    };
    
    button.addEventListener('mouseenter', handleMouseEnter);
    button.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      button.removeEventListener('mouseenter', handleMouseEnter);
      button.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const baseClassName = `relative overflow-hidden cursor-pointer ${className}`;

  // 根据是否有 href 属性来决定使用 <a> 还是 <button>
  if (href) {
    return (
      <a
        ref={buttonRef}
        href={href}
        target={target}
        rel={rel}
        onClick={onClick}
        className={baseClassName}
      >
        <span className="relative z-10">{children}</span>
        <div
          ref={rippleRef}
          className="absolute w-20 h-20 -translate-x-1/2 -translate-y-1/2 bg-sky-900/90 rounded-full pointer-events-none"
        />
      </a>
    );
  }

  return (
    <button
      ref={buttonRef}
      onClick={onClick}
      className={baseClassName}
      type="button"
    >
      <span className="relative z-10">{children}</span>
      <div
        ref={rippleRef}
        className="absolute w-20 h-20 -translate-x-1/2 -translate-y-1/2 bg-sky-900/90 rounded-full pointer-events-none"
      />
    </button>
  );
};

export default ButtonWithHoverEffect;